import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Alert, Button, Box } from '@mui/material';
import i18n from 'i18next';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary that catches React DOM errors caused by browser translation extensions
 * (like Google Translate) modifying the DOM outside of React's control.
 *
 * Common error: "Failed to execute 'insertBefore' on 'Node'"
 *
 * Usage:
 * <TranslationErrorBoundary>
 *   <YourComponent />
 * </TranslationErrorBoundary>
 */
class TranslationErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Check if this is a translation extension error
        const isTranslationError =
            error.message.includes('insertBefore') ||
            error.message.includes('removeChild') ||
            error.message.includes('appendChild');

        // Chrome stamps this class on <html> while a page is machine-translated —
        // the strongest signal that an extension actually rewrote the DOM.
        const domIsTranslated = /translated-(ltr|rtl)/.test(document.documentElement.className);

        // Report with the component stack: without it the crash site is untraceable in
        // production (console shows only the minified error, and Sentry never sees
        // boundary-caught exceptions).
        Sentry.captureException(error, {
            tags: {
                errorBoundary: 'translation',
                isTranslationError: String(isTranslationError),
                domTranslated: String(domIsTranslated),
                uiLanguage: i18n.language,
            },
            extra: {
                componentStack: errorInfo.componentStack,
                documentLang: document.documentElement.lang,
                navigatorLanguage: typeof navigator !== 'undefined' ? navigator.language : undefined,
            },
        });

        if (isTranslationError) {
            console.warn(
                'TranslationErrorBoundary caught a DOM manipulation error. ' +
                    'This is likely caused by a browser translation extension. ' +
                    'Consider disabling translation extensions or refreshing the page.',
                error,
                errorInfo.componentStack
            );
        } else {
            console.error('TranslationErrorBoundary caught an error:', error, errorInfo);
        }
    }

    handleRetry = (): void => {
        this.setState({ hasError: false, error: null });
    };

    handleRefresh = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const isTranslationError =
                this.state.error?.message.includes('insertBefore') ||
                this.state.error?.message.includes('removeChild') ||
                this.state.error?.message.includes('appendChild');

            const t = i18n.t.bind(i18n);
            return (
                <Box sx={{ p: 2 }}>
                    <Alert
                        severity="warning"
                        action={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button color="inherit" size="small" onClick={this.handleRetry}>
                                    {t('action.retry')}
                                </Button>
                                <Button color="inherit" size="small" onClick={this.handleRefresh}>
                                    {t('action.refresh')}
                                </Button>
                            </Box>
                        }
                    >
                        {isTranslationError ? t('error.browserTranslation') : t('error.generic')}
                    </Alert>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default TranslationErrorBoundary;
